import { get } from 'lodash';
import {
  ApplicationNavigateHandlerMapProps,
  AssignmentTypeStrategy,
  CoursePlanClickSettings,
  CoursePlanTitleClickProps,
  SherEvolAssessmentStartProps,
  SherEvolAssignmentCreateProps,
  SortCatalogItems
} from '../model';
import { OsmosisAssignmentPlayerQueryParams } from '../../pages/osmosis-assignment-player/osmosis-assignment-player.constants';
import { RoutePath } from '../../components/app/app.constants';
import { addSearchParams } from '../../utilities/app.utilities';
import { Application } from '../../apis/eols-app-link/eols-app-link.constants';
import { getCatalogItemFromSyllabusItem, getOsmosisVideo, getOsmosisVideoClone } from '../../utilities/assignment-types-shared.utilities';
import {
  navigateExternal,
  navigateToPerformanceReport,
  redirectInternal,
  commonAssignmentInfoHandler,
  sherEvolPerformanceViewRedirect,
  getExternalEntityCommon
} from '../utils/common.utils';
import { getOsmosisVideoAssignmentInfo } from '../../pages/osmosis-video-editor/osmosis-video.utilities';
import { RecContentItemTitleHandlerProps, RecContentItemTitleHandlerPropsClone } from '../../models/assignment-types-shared.models';

const sortBySequenceIndex = (props: SortCatalogItems) => {
  const { contentItem, itemSequenceMap } = props;
  if (!itemSequenceMap || !contentItem.attributes || !contentItem.attributes.contentId) {
    return null;
  }
  const sequenceItem = itemSequenceMap[contentItem.attributes.contentId];
  if (!sequenceItem) {
    return null;
  }
  return sequenceItem.sequenceIndex;
};

export const osmosisVideo: AssignmentTypeStrategy = {
  coursePlanTitleClick: (props: CoursePlanTitleClickProps) => {
    const { defArgs, isOsmosisVideosEnabled, assignment, syllabusItem, catalogItem } = props;
    const { redirect } = defArgs.props;
    if (!isOsmosisVideosEnabled) {
      return Promise.resolve();
    }
    return Promise.resolve(
      redirect(addSearchParams(RoutePath.OSMOSIS_ASSIGNMENT_PLAYER, {
        [OsmosisAssignmentPlayerQueryParams.ref]: get(defArgs.props, 'location.pathname'),
        [OsmosisAssignmentPlayerQueryParams.syllabusItemId]: syllabusItem.id,
        [OsmosisAssignmentPlayerQueryParams.assignmentId]: assignment ? assignment.id : null,
        [OsmosisAssignmentPlayerQueryParams.catalogItemId]: catalogItem ? catalogItem.id : null
      }))
    );
  },
  coursePlanClickSettings: (props: CoursePlanClickSettings) => {
    const { defArgs, sharedSearchParams } = props;
    const { redirect } = defArgs.props;
    redirect(addSearchParams(RoutePath.OSMOSIS_VIDEO_EDITOR, sharedSearchParams));
  },
  coursePlanPerformanceReportClick: navigateToPerformanceReport,
  catalogPageTitleClick: (props) => {
    const { defArgs, contentItem, isOsmosisVideosEnabled } = props;
    const { redirect } = defArgs.props;
    if (isOsmosisVideosEnabled) {
      redirect(addSearchParams(RoutePath.OSMOSIS_ASSIGNMENT_PLAYER, {
        [OsmosisAssignmentPlayerQueryParams.ref]: get(defArgs.props, 'location.pathname'),
        [OsmosisAssignmentPlayerQueryParams.catalogItemId]: contentItem.id
      }));
    }
  },
  assignmentOverviewTitleClick: (props: ApplicationNavigateHandlerMapProps) => {
    const { syllabusItem, defArgs, isDifferentCourse } = props;
    const { catalog } = defArgs.props;
    const catalogItem = getCatalogItemFromSyllabusItem(catalog, syllabusItem);
    if (isDifferentCourse) {
      navigateExternal(props);
    } else {
      redirectInternal(props, RoutePath.OSMOSIS_ASSIGNMENT_PLAYER, {
        [OsmosisAssignmentPlayerQueryParams.catalogItemId]: catalogItem ? catalogItem.id : null
      });
    }
  },
  assignmentOverviewPerformanceReportClick: (props: ApplicationNavigateHandlerMapProps) => {
    if (props.isDifferentCourse) {
      navigateExternal(props);
    } else {
      redirectInternal(props);
    }
  },
  navigateToAppHandlerMap: () => Application.SHER_EVOL,
  navigateToAppForAssignmentPerformanceHandlerMap: () => Application.SHER_EVOL,
  assignmentInfoHandler: (props) => commonAssignmentInfoHandler(getOsmosisVideoAssignmentInfo)(props),
  sortCatalogItemsWithoutTaxonomy: sortBySequenceIndex,
  sortCatalogItemsWithoutTaxonomyClone: sortBySequenceIndex,
  recContentItemTitleHandler: (props: RecContentItemTitleHandlerProps) => {
    const osmosisVideoEntity = getOsmosisVideo(props.contentItem, props.catalog);
    if (osmosisVideoEntity) {
      return osmosisVideoEntity._parsedData.title;
    }
    return null;
  },
  recContentItemTitleHandlerClone: (props: RecContentItemTitleHandlerPropsClone) => {
    const osmosisVideoEntity = getOsmosisVideoClone(props.contentItem, props.externalEntitiesMap);
    if (osmosisVideoEntity) {
      return osmosisVideoEntity._parsedData.title;
    }
    return null;
  },
  sherEvolAssignmentCreate: (props: SherEvolAssignmentCreateProps) => {
    const { sharedSearchParams, redirect } = props;
    redirect(addSearchParams(RoutePath.OSMOSIS_VIDEO_EDITOR, sharedSearchParams));
  },
  sherEvolAssignmentPerformanceView: sherEvolPerformanceViewRedirect,
  sherEvolAssessmentStart: (props: SherEvolAssessmentStartProps) => {
    const playerPath = RoutePath.OSMOSIS_ASSIGNMENT_PLAYER;
    const { redirect, searchParams, catalogItem } = props;
    const updatedSearchParams = {
      ...searchParams,
      [OsmosisAssignmentPlayerQueryParams.catalogItemId]: catalogItem?.id
    };
    redirect(addSearchParams(playerPath, updatedSearchParams));
  },
  getExternalEntity: getExternalEntityCommon,
  getExternalEntityClone: getExternalEntityCommon,
  isScorable: true
};
